import React, { useState, useEffect } from 'react';
import { Bot, Eye, User, Calendar, Star, AlertTriangle, Send, Loader2, CheckCircle } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { toast } from '../hooks/use-toast';
import { apiService } from '../services/apiService';

const AIAssistedObservation = ({ onObservationCreated, onClose, preselectedEmployee = null }) => {
  const [step, setStep] = useState('chat'); // 'chat', 'review', 'submitted'
  const [observationType, setObservationType] = useState(null); // 'formal', 'quick'
  const [chatMessages, setChatMessages] = useState([]);
  const [inputValue, setInputValue] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [extractedData, setExtractedData] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    // Start with a welcome message
    const welcomeMessage = preselectedEmployee 
      ? `Hi! I'll help you record an observation for ${preselectedEmployee.first_name} ${preselectedEmployee.last_name}. You can tell me about:\n\n• **Formal Observation** - "I want to record a formal classroom observation with ratings"\n• **Quick Note** - "I noticed excellent student engagement" or "There was a dress code issue"\n\nWhat would you like to record?`
      : `Hi! I'll help you record an observation. You can tell me about:\n\n• **Formal Observation** - "I want to record a formal classroom observation for John Smith"\n• **Quick Note** - "I noticed excellent student engagement from Mary Johnson" or "There was a dress code issue with Bob Wilson"\n\nWhat observation would you like to record?`;

    setChatMessages([{
      id: 1,
      type: 'ai',
      content: welcomeMessage,
      timestamp: new Date()
    }]);

    if (preselectedEmployee) {
      setExtractedData({ employee: preselectedEmployee });
    }
  }, [preselectedEmployee]);

  const sendMessage = async (message) => {
    if (!message.trim()) return;

    const userMessage = {
      id: Date.now(),
      type: 'user',
      content: message,
      timestamp: new Date()
    };

    setChatMessages(prev => [...prev, userMessage]);
    setInputValue('');
    setIsTyping(true);

    try {
      const response = await apiService.makeRequest('/api/ai/chat', {
        method: 'POST',
        body: JSON.stringify({
          message: message,
          context: { 
            workflow: 'observation_entry',
            employee: preselectedEmployee ? {
              id: preselectedEmployee.id,
              name: `${preselectedEmployee.first_name} ${preselectedEmployee.last_name}`
            } : null
          }
        })
      });

      const aiMessage = {
        id: Date.now() + 1,
        type: 'ai',
        content: response.response,
        timestamp: new Date(),
        actions: response.actions || []
      };

      setChatMessages(prev => [...prev, aiMessage]);

      // Check if we have enough information to create the observation
      if (response.actions && response.actions.some(action => action.type === 'create_observation')) {
        const createAction = response.actions.find(action => action.type === 'create_observation');
        setExtractedData(prev => ({ ...prev, ...createAction.data }));
        setObservationType(createAction.data.type || 'quick');
        setStep('review');
      }

    } catch (error) {
      console.error('Failed to send message:', error);
      const errorMessage = {
        id: Date.now() + 1,
        type: 'ai',
        content: "I'm sorry, I encountered an error. Please try again or fill out the form manually.",
        timestamp: new Date(),
        isError: true
      };
      setChatMessages(prev => [...prev, errorMessage]);
    } finally {
      setIsTyping(false);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    sendMessage(inputValue);
  };

  const submitObservation = async () => {
    try {
      setIsSubmitting(true);
      
      const observationData = {
        ...extractedData,
        employee_id: extractedData.employee?.id || extractedData.employee_id,
        type: observationType,
        date: new Date().toISOString(),
        created_via_ai: true
      };

      // Remove the employee object from the data since we only need the ID
      delete observationData.employee;

      const result = await apiService.createObservation(observationData);
      
      setStep('submitted');
      toast({
        title: 'Observation Recorded',
        description: `Your ${observationType} observation has been saved successfully.`
      });

      if (onObservationCreated) {
        onObservationCreated(result);
      }

    } catch (error) {
      console.error('Failed to submit observation:', error);
      toast({
        title: 'Submission Failed',
        description: 'Failed to save your observation. Please try again.',
        variant: 'destructive'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const editData = (field, value) => {
    setExtractedData(prev => ({ ...prev, [field]: value }));
  };

  if (step === 'submitted') {
    return (
      <Card className="w-full max-w-2xl mx-auto">
        <CardContent className="p-8 text-center">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <CheckCircle className="w-8 h-8 text-green-600" />
          </div>
          <h3 className="text-xl font-semibold text-gray-900 mb-2">Observation Recorded!</h3>
          <p className="text-gray-600 mb-6">
            Your {observationType} observation has been saved successfully and will be available in the employee's record.
          </p>
          <div className="flex justify-center space-x-4">
            <Button onClick={onClose} variant="outline">
              Close
            </Button>
            <Button 
              onClick={() => {
                setStep('chat');
                setObservationType(null);
                setChatMessages([{
                  id: 1,
                  type: 'ai',
                  content: "Would you like to record another observation?",
                  timestamp: new Date()
                }]);
                setExtractedData(preselectedEmployee ? { employee: preselectedEmployee } : {});
              }}
              className="bg-yellow-500 hover:bg-yellow-600 text-black"
            >
              New Observation
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (step === 'review') {
    return (
      <Card className="w-full max-w-2xl mx-auto">
        <CardHeader>
          <CardTitle className="flex items-center">
            <Eye className="w-5 h-5 mr-2 text-yellow-600" />
            Review Your {observationType === 'formal' ? 'Formal' : 'Quick'} Observation
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
            <p className="text-sm text-yellow-800">
              I've extracted the following information from our conversation. Please review and make any necessary changes:
            </p>
          </div>

          {/* Employee Information */}
          <div>
            <Label>Employee</Label>
            <div className="mt-1 p-3 bg-gray-50 rounded-lg">
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 bg-gradient-to-r from-gray-600 to-gray-800 rounded-full flex items-center justify-center">
                  <span className="text-white font-semibold text-sm">
                    {(extractedData.employee?.first_name || ' ')[0]}{(extractedData.employee?.last_name || ' ')[0]}
                  </span>
                </div>
                <div>
                  <p className="font-medium text-gray-900">
                    {extractedData.employee?.first_name} {extractedData.employee?.last_name}
                  </p>
                  <p className="text-sm text-gray-500">{extractedData.employee?.position_title}</p>
                </div>
              </div>
            </div>
          </div>

          {observationType === 'formal' ? (
            // Formal observation fields
            <>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="overall_rating">Overall Rating (1-5)</Label>
                  <Input
                    id="overall_rating"
                    type="number"
                    min="1"
                    max="5"
                    value={extractedData.overall_rating || ''}
                    onChange={(e) => {
                      const value = e.target.value;
                      if (value === '') {
                        editData('overall_rating', '');
                      } else {
                        const parsed = parseInt(value);
                        if (!isNaN(parsed)) {
                          const clamped = Math.max(1, Math.min(5, parsed));
                          editData('overall_rating', clamped);
                        }
                      }
                    }}
                  />
                </div>
                <div>
                  <Label htmlFor="follow_up_required">Follow-up Required</Label>
                  <select
                    id="follow_up_required"
                    value={extractedData.follow_up_required ? 'yes' : 'no'}
                    onChange={(e) => editData('follow_up_required', e.target.value === 'yes')}
                    className="w-full mt-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-yellow-500"
                  >
                    <option value="no">No</option>
                    <option value="yes">Yes</option>
                  </select>
                </div>
              </div>

              <div>
                <Label htmlFor="detailed_feedback">Detailed Feedback</Label>
                <Textarea
                  id="detailed_feedback"
                  value={extractedData.detailed_feedback || ''}
                  onChange={(e) => editData('detailed_feedback', e.target.value)}
                  placeholder="Detailed observation notes and feedback"
                  rows={4}
                />
              </div>

              <div>
                <Label htmlFor="strengths">Strengths</Label>
                <Textarea
                  id="strengths"
                  value={extractedData.strengths || ''}
                  onChange={(e) => editData('strengths', e.target.value)}
                  placeholder="Areas of strength observed"
                  rows={2}
                />
              </div>

              <div>
                <Label htmlFor="improvement_areas">Areas for Improvement</Label>
                <Textarea
                  id="improvement_areas"
                  value={extractedData.improvement_areas || ''}
                  onChange={(e) => editData('improvement_areas', e.target.value)}
                  placeholder="Areas that could be improved"
                  rows={2}
                />
              </div>
            </>
          ) : (
            // Quick observation fields
            <>
              <div>
                <Label htmlFor="note">Observation Note</Label>
                <Textarea
                  id="note"
                  value={extractedData.note || ''}
                  onChange={(e) => editData('note', e.target.value)}
                  placeholder="Brief observation note"
                  rows={3}
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="sentiment">Sentiment</Label>
                  <select
                    id="sentiment"
                    value={extractedData.sentiment || 'neutral'}
                    onChange={(e) => editData('sentiment', e.target.value)}
                    className="w-full mt-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-yellow-500"
                  >
                    <option value="positive">Positive</option>
                    <option value="neutral">Neutral</option>
                    <option value="negative">Negative</option>
                  </select>
                </div>
                <div>
                  <Label htmlFor="category">Category</Label>
                  <select
                    id="category"
                    value={extractedData.category || ''}
                    onChange={(e) => editData('category', e.target.value)}
                    className="w-full mt-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-yellow-500"
                  >
                    <option value="">Select category</option>
                    <option value="classroom_management">Classroom Management</option>
                    <option value="student_engagement">Student Engagement</option>
                    <option value="lesson_delivery">Lesson Delivery</option>
                    <option value="dress_code">Dress Code</option>
                    <option value="professionalism">Professionalism</option>
                    <option value="other">Other</option>
                  </select>
                </div>
              </div>

              {extractedData.sentiment === 'negative' && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                  <div className="flex items-center">
                    <AlertTriangle className="w-5 h-5 text-red-600 mr-2" />
                    <span className="text-sm font-medium text-red-800">Negative Observation</span>
                  </div>
                  <p className="text-sm text-red-700 mt-1">
                    This observation will be flagged for follow-up and may require additional documentation.
                  </p>
                </div>
              )}
            </>
          )}

          <div className="flex justify-between pt-4">
            <Button
              variant="outline"
              onClick={() => setStep('chat')}
            >
              Back to Chat
            </Button>
            <Button
              onClick={submitObservation}
              disabled={isSubmitting || !extractedData.employee}
              className="bg-yellow-500 hover:bg-yellow-600 text-black"
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                'Save Observation'
              )}
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="w-full max-w-2xl mx-auto h-96 flex flex-col">
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center">
          <Bot className="w-5 h-5 mr-2 text-yellow-600" />
          AI-Assisted Observation Entry
        </CardTitle>
      </CardHeader>

      <CardContent className="flex-1 p-0 overflow-hidden">
        <div className="h-full p-4 overflow-y-auto">
          <div className="space-y-4">
            {chatMessages.map((message) => (
              <div key={message.id} className={`flex ${message.type === 'user' ? 'justify-end' : 'justify-start'}`}>
                <div className={`max-w-xs lg:max-w-md px-4 py-2 rounded-lg ${
                  message.type === 'user' 
                    ? 'bg-yellow-500 text-black' 
                    : message.isError 
                      ? 'bg-red-50 text-red-800 border border-red-200'
                      : 'bg-gray-100 text-gray-900'
                }`}>
                  <p className="text-sm whitespace-pre-wrap">{message.content}</p>
                  <div className="mt-1">
                    <span className="text-xs opacity-70">
                      {message.timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                    </span>
                  </div>
                </div>
              </div>
            ))}
            
            {isTyping && (
              <div className="flex justify-start">
                <div className="bg-gray-100 px-4 py-2 rounded-lg">
                  <div className="flex items-center space-x-1">
                    <div className="flex space-x-1">
                      <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce"></div>
                      <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0.1s' }}></div>
                      <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
                    </div>
                    <span className="text-xs text-gray-500 ml-2">Processing...</span>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </CardContent>

      <div className="p-4 border-t">
        <form onSubmit={handleSubmit} className="flex space-x-2">
          <Input
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            placeholder="Describe your observation..."
            disabled={isTyping}
            className="flex-1"
          />
          <Button 
            type="submit" 
            disabled={isTyping || !inputValue.trim()}
            className="bg-yellow-500 hover:bg-yellow-600 text-black"
          >
            {isTyping ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Send className="w-4 h-4" />
            )}
          </Button>
        </form>
      </div>
    </Card>
  );
};

export default AIAssistedObservation;